import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import config from "@/i18n/config";
import { buildFullUrl } from "@/i18n/detectLanguage";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  lang?: string;
  /** The base path for this page (without lang prefix), e.g. "/memberships" */
  path?: string;
  /** CMS pages supply only translations that actually exist. */
  alternates?: { lang: string; href: string }[];
}

const SEOHead = ({
  title = "Thailand Elite – Long Stay Visa & Privileges",
  description = "Become a Thailand Elite member. 5 to 20 year multi-entry visa, VIP services, airport fast-track, and exclusive privileges. Apply online today.",
  canonical,
  ogImage = "/og-image.jpg",
  lang,
  path,
  alternates,
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const currentLang = lang || i18n.language;

  // Auto-generate canonical from path if not explicitly provided
  const resolvedCanonical = canonical || (path ? buildFullUrl(path, currentLang) : config.mainDomain);

  // Build hreflang alternate links for SEO
  const hreflangLinks = alternates ?? (path && !/^\/(blog|news)\//.test(path)
    ? config.supportedLanguages.map((langCode) => ({
        lang: langCode,
        href: buildFullUrl(path, langCode),
      }))
    : []);
  const defaultAlternate = hreflangLinks.find((alt) => alt.lang === "en")?.href
    || hreflangLinks.find((alt) => alt.lang === currentLang)?.href;

  return (
    <Helmet htmlAttributes={{ lang: currentLang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={resolvedCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {hreflangLinks.map((alt) => (
        <link
          key={alt.lang}
          rel="alternate"
          hrefLang={alt.lang}
          href={alt.href}
        />
      ))}
      {defaultAlternate && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={defaultAlternate}
        />
      )}
    </Helmet>
  );
};

export default SEOHead;
